import { useEffect, useState } from 'react';
import { Spin, Tag } from 'antd';
import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import { aiChatApi } from '~/services/ai-chat';

interface Session {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

interface SessionMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: any[];
  pendingChanges?: any[];
  createdAt: string;
}

interface SessionDetail {
  id: string;
  title: string;
  messages: SessionMessage[];
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatValue(value: any): string {
  if (value == null) return '—';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(value);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (Array.isArray(value)) return `${value.length} items`;
  return String(value).replace(/_/g, ' ');
}

function MutationCard({ change }: { change: any }) {
  const fields = Object.keys(change.after || {});

  return (
    <div className="border border-gray-200 p-2 mb-1.5 bg-white text-[11px]">
      <div className="flex items-center gap-1.5 mb-1">
        <Tag color={ACTION_COLORS[change.action]} className="text-[9px] m-0 leading-tight px-1">
          {change.action}
        </Tag>
        <span className="font-medium truncate">{change.label}</span>
      </div>
      {change.context && (
        <div className="text-gray-400 mb-1">
          {change.context.client && <span>{change.context.client}</span>}
          {change.context.bookingRef && <span> · #{change.context.bookingRef}</span>}
          {change.context.serial && <span> · {change.context.serial}</span>}
        </div>
      )}
      {fields.length > 0 && (
        <div className="border-t border-gray-100 pt-1 space-y-0.5">
          {fields.map((key) => (
            <div key={key} className="flex items-center gap-1">
              <span className="text-gray-400">{key}:</span>
              {change.before?.[key] != null && (
                <>
                  <span className="line-through text-red-300">{formatValue(change.before[key])}</span>
                  <RightOutlined className="text-[7px] text-gray-300" />
                </>
              )}
              <span className="font-medium">{formatValue(change.after[key])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SessionItem({
  session,
  onSelect,
}: {
  session: Session;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(session.id)}
      className="w-full text-left px-3 py-2 border border-gray-200 mb-1.5 hover:border-black hover:bg-gray-50 transition-colors"
    >
      <div className="text-xs font-medium truncate">{session.title || 'Untitled session'}</div>
      <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(session.createdAt)}</div>
    </button>
  );
}

function SessionDetailView({
  detail,
  onBack,
}: {
  detail: SessionDetail;
  onBack: () => void;
}) {
  // Extract all mutations from messages
  const mutations = detail.messages
    .filter((m) => m.pendingChanges && m.pendingChanges.length > 0)
    .flatMap((m) => m.pendingChanges!);

  return (
    <div>
      <button
        onClick={onBack}
        className="text-xs text-gray-500 hover:text-black mb-3 flex items-center gap-1"
      >
        ← Back to sessions
      </button>
      <div className="text-xs font-bold mb-1 truncate">{detail.title || 'Untitled'}</div>
      <div className="text-[10px] text-gray-400 mb-3">
        {detail.messages.length} messages
        {mutations.length > 0 && ` · ${mutations.length} mutations`}
      </div>

      {mutations.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
            Mutations ({mutations.length})
          </div>
          {mutations.map((change: any, i: number) => (
            <MutationCard key={change.id || i} change={change} />
          ))}
        </div>
      )}

      <div>
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
          Conversation
        </div>
        {detail.messages.map((msg) => (
          <div key={msg.id} className="mb-2">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span
                className={`text-[10px] font-bold uppercase ${
                  msg.role === 'user' ? 'text-black' : 'text-gray-400'
                }`}
              >
                {msg.role === 'user' ? 'You' : 'Oz'}
              </span>
              <span className="text-[9px] text-gray-300">{formatDate(msg.createdAt)}</span>
            </div>
            <div className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-4">
              {msg.content}
            </div>
            {msg.toolCalls && msg.toolCalls.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-1">
                {msg.toolCalls.map((tc: any, i: number) => (
                  <Tag key={i} className="text-[9px] m-0">
                    {tc.tool}
                    {tc.pending && ' ⏳'}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export const HistoryPanel = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      aiChatApi
        .getSessions()
        .then(setSessions)
        .catch(() => setSessions([]))
        .finally(() => setLoading(false));
    } else {
      setSelectedDetail(null);
    }
  }, [isOpen]);

  const handleSelectSession = async (id: string) => {
    setLoading(true);
    try {
      const detail = await aiChatApi.getSession(id);
      setSelectedDetail(detail);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed right-0 top-0 h-full bg-white border-l-2 border-black z-40 transition-transform duration-200 flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{ width: 380 }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wider">History</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-black">
          <CloseOutlined />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loading && (
          <div className="flex justify-center mt-8">
            <Spin size="small" />
          </div>
        )}

        {!loading && !selectedDetail && (
          <>
            {sessions.length === 0 ? (
              <p className="text-xs text-gray-400 text-center mt-8">
                No chat history yet. Start a conversation with Oz.
              </p>
            ) : (
              sessions.map((s) => (
                <SessionItem key={s.id} session={s} onSelect={handleSelectSession} />
              ))
            )}
          </>
        )}

        {!loading && selectedDetail && (
          <SessionDetailView
            detail={selectedDetail}
            onBack={() => setSelectedDetail(null)}
          />
        )}
      </div>
    </div>
  );
};
